import { isSupportedSchemaVersion } from '../poolEvents';
import { ClipPlayer } from './clipPlayer';

/**
 * HITL-1786 (HITL 8.0.0): the guest's audio for a hear-and-confirm card,
 * pushed by live-transcriber (contract D11, §4.8).
 *
 * task-router asks live-transcriber for the card's turns as it assigns the
 * card; live-transcriber posts one `assist_audio_clip` per assigned agent to
 * that agent's `TASK_CHANNEL_<user id>`, the socket the pool already holds.
 * The console asks for nothing and answers nothing: a clip arrives (about
 * 0.75 s after the card on staging) or it does not, and the card is
 * answerable from the text either way.
 *
 * Audio never goes into Redux: a 3 s clip is ~128 KB of base64. The live
 * card's clip is held here, in one slot, and dropped with the card.
 *
 * The card comes from task-router and the clip from live-transcriber, two hops
 * with no ordering between them, so a clip can land before its card. That clip
 * waits in a second slot for `ASSIST_EARLY_CLIP_HOLD_MS` and is attached when
 * its card shows (`adoptEarly`); past that it is stale and dropped.
 */

export const ASSIST_AUDIO_CLIP_EVENT = 'assist_audio_clip';
/** live-transcriber's AssemblyAI audio: 16 kHz mono s16le. */
export const CLIP_SAMPLE_RATE = 16000;
export const CLIP_ENCODING = 'pcm_s16le';
/** How long a clip that arrived before its card waits for it. */
export const ASSIST_EARLY_CLIP_HOLD_MS = 5000;

export interface IAssistClip {
  requestId: string;
  samples: Int16Array;
  sampleRate: number;
  durationMs: number;
}

export type AssistClipIgnoreReason =
  | 'unsupported_schema'
  | 'malformed'
  | 'unsupported_encoding'
  | 'empty';

export const base64ToInt16 = (payload: string): Int16Array => {
  if (!payload) return new Int16Array(0);
  const decoded = atob(payload);
  const usable = decoded.length - (decoded.length % 2);
  const bytes = new Uint8Array(usable);
  for (let i = 0; i < usable; i += 1) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
};

// The clip's wire format is defined in docs/assist-contract.md (§5.4).
/** Pure: one parsed `assist_audio_clip` message to a playable clip. */
export const parseAssistClip = (
  message: any
): { clip: IAssistClip } | { ignored: AssistClipIgnoreReason } => {
  const data = message?.data;
  if (!data || !isSupportedSchemaVersion(data.schema_version)) {
    return { ignored: 'unsupported_schema' };
  }
  if (
    typeof data.request_id !== 'string' ||
    !data.request_id ||
    typeof data.audio_b64 !== 'string'
  ) {
    return { ignored: 'malformed' };
  }
  if (data.encoding !== undefined && data.encoding !== CLIP_ENCODING) {
    return { ignored: 'unsupported_encoding' };
  }
  const sampleRate =
    Number.isInteger(data.sample_rate) && data.sample_rate > 0
      ? data.sample_rate
      : CLIP_SAMPLE_RATE;
  let samples: Int16Array;
  try {
    samples = base64ToInt16(data.audio_b64);
  } catch {
    return { ignored: 'malformed' };
  }
  if (!samples.length) return { ignored: 'empty' };
  return {
    clip: {
      requestId: data.request_id,
      samples,
      sampleRate,
      durationMs: Math.round((samples.length / sampleRate) * 1000),
    },
  };
};

let held: IAssistClip | null = null;
let early: { clip: IAssistClip; at: number } | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** The live card's clip: one slot, since there is one card at a time. */
export const assistClipStore = {
  get: (requestId: string): IAssistClip | null =>
    held?.requestId === requestId ? held : null,
  put(clip: IAssistClip) {
    held = clip;
    notify();
  },
  drop(requestId: string) {
    if (early?.clip.requestId === requestId) early = null;
    if (held?.requestId !== requestId) return;
    held = null;
    notify();
  },
  /** A clip whose card has not shown yet: the latest one waits for it. */
  holdEarly(clip: IAssistClip, at: number) {
    early = { clip, at };
  },
  /**
   * Called as a card shows: attaches the clip that arrived before it, if it is
   * this card's and still fresh. Returns the clip and how long it waited.
   */
  adoptEarly(
    requestId: string,
    now: number
  ): { clip: IAssistClip; waitedMs: number } | null {
    const waiting = early;
    if (!waiting || waiting.clip.requestId !== requestId) return null;
    early = null;
    const waitedMs = now - waiting.at;
    if (waitedMs > ASSIST_EARLY_CLIP_HOLD_MS) return null;
    held = waiting.clip;
    notify();
    return { clip: waiting.clip, waitedMs };
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Tests only. */
  reset() {
    held = null;
    early = null;
    listeners.clear();
  },
};

let sharedPlayer: ClipPlayer | null = null;

/** One player for the lobby: one clip at a time. */
export const getAssistClipPlayer = (): ClipPlayer => {
  if (!sharedPlayer) sharedPlayer = new ClipPlayer();
  return sharedPlayer;
};

/** Tests only. */
export const setAssistClipPlayer = (player: ClipPlayer | null) => {
  sharedPlayer = player;
};
